import type { Profile } from '@/types/db';
import type { TimelineEventWithExtras } from '@/features/timeline/lib/timelineQueries';

export type SuggestionCategory =
  | 'medication'
  | 'vaccine'
  | 'lab'
  | 'visit'
  | 'diagnosis'
  | 'allergy'
  | 'scan'
  | 'vitals'
  | 'general';

export type ProfileFactKey =
  | 'hasPrescriptions'
  | 'hasActiveMedications'
  | 'hasStoppedMeds'
  | 'hasChronicMeds'
  | 'hasAntibiotics'
  | 'hasVaccines'
  | 'hasLabs'
  | 'hasAbnormalLabs'
  | 'hasVisits'
  | 'hasScans'
  | 'hasVitals'
  | 'hasDiagnoses'
  | 'hasAllergies'
  | 'hasRecentEvent';

export type ProfileFacts = Record<ProfileFactKey, boolean>;

export interface SuggestionEntry {
  text: string;
  category: SuggestionCategory;
  /** When source is `personalized`, base score is 100; static is 50. */
  source: 'static' | 'personalized';
  /** Facts that, if all true on the profile, are required for this entry to render. */
  requires?: ProfileFactKey[];
  /** Facts that, if any are true, boost this entry's score by +25 (capped). */
  boostsOn?: ProfileFactKey[];
}

export interface RankedSuggestion {
  text: string;
  category: SuggestionCategory;
  source: 'static' | 'personalized';
  score: number;
}

interface ClickRecord {
  count: number;
  last_clicked_at: string;
}

type ClickLedger = Record<string, ClickRecord>;

const ANTIBIOTIC_STEMS = [
  'amoxicillin',
  'amoxil',
  'augmentin',
  'clavulan',
  'azithromycin',
  'zithromax',
  'klacid',
  'clarithromycin',
  'erythromycin',
  'cipro',
  'ciprofloxacin',
  'levofloxacin',
  'tavanic',
  'levaquin',
  'ofloxacin',
  'doxycycline',
  'tetracycline',
  'metronidazole',
  'flagyl',
  'penicillin',
  'cefdinir',
  'cefuroxime',
  'cefixime',
  'cephalexin',
  'ceftriaxone',
  'cefotax',
  'rocephin',
  'zinnat',
  'sulfamethoxazole',
  'septra',
  'bactrim',
  'nitrofurantoin',
  'clindamycin',
  'fucidin',
  'fusidic',
];

function isAntibiotic(drugName: string | null): boolean {
  if (!drugName) return false;
  const lower = drugName.toLowerCase();
  return ANTIBIOTIC_STEMS.some((stem) => lower.includes(stem));
}

function firstName(fullName: string): string {
  const trimmed = fullName.trim();
  if (!trimmed) return 'this profile';
  return trimmed.split(/\s+/)[0];
}

export function computeProfileFacts(
  profile: Profile,
  events: TimelineEventWithExtras[],
): ProfileFacts {
  const now = Date.now();
  const RECENT_DAYS = 90;
  const recentCutoff = now - RECENT_DAYS * 86400000;

  let hasPrescriptions = false;
  let hasActiveMedications = false;
  let hasStoppedMeds = false;
  let hasChronicMeds = false;
  let hasAntibiotics = false;
  let hasVaccines = false;
  let hasLabs = false;
  let hasAbnormalLabs = false;
  let hasVisits = false;
  let hasScans = false;
  let hasVitals = false;
  let hasDiagnoses = false;
  let hasRecentEvent = false;

  const today = new Date().toISOString().slice(0, 10);

  for (const event of events) {
    const eventDateMs = event.event_date
      ? new Date(event.event_date).getTime()
      : new Date(event.created_at).getTime();
    if (eventDateMs >= recentCutoff) hasRecentEvent = true;

    if (event.diagnosis && event.diagnosis.trim().length > 0) {
      hasDiagnoses = true;
    }

    switch (event.event_type) {
      case 'prescription':
        hasPrescriptions = true;
        break;
      case 'lab_report':
        hasLabs = true;
        break;
      case 'doctor_visit':
        hasVisits = true;
        break;
      case 'vaccine':
        hasVaccines = true;
        break;
      case 'scan':
        hasScans = true;
        break;
      case 'vitals':
        hasVitals = true;
        break;
    }

    for (const med of event.medications) {
      if (med.is_stopped) {
        hasStoppedMeds = true;
      } else if (!med.end_date || med.end_date >= today) {
        hasActiveMedications = true;
      }

      if ((med.duration_days ?? 0) >= 30) {
        hasChronicMeds = true;
      }

      if (isAntibiotic(med.drug_name)) {
        hasAntibiotics = true;
      }
    }

    for (const flag of event.ai_flags ?? []) {
      if (
        flag.code === 'value_out_of_range' ||
        flag.code === 'critical_finding_in_conclusion' ||
        flag.code === 'vital_out_of_range'
      ) {
        hasAbnormalLabs = true;
      }
    }
  }

  return {
    hasPrescriptions,
    hasActiveMedications,
    hasStoppedMeds,
    hasChronicMeds,
    hasAntibiotics,
    hasVaccines,
    hasLabs,
    hasAbnormalLabs,
    hasVisits,
    hasScans,
    hasVitals,
    hasDiagnoses,
    hasAllergies: profile.allergies.length > 0,
    hasRecentEvent,
  };
}

const STATIC_POOL: readonly SuggestionEntry[] = [
  { text: "What medications is {name} currently taking?", category: 'medication', source: 'static', boostsOn: ['hasActiveMedications'] },
  { text: "What's the most recurrent medication?", category: 'medication', source: 'static', boostsOn: ['hasPrescriptions'] },
  { text: "What are the doses of {name}'s active medications?", category: 'medication', source: 'static', boostsOn: ['hasActiveMedications'] },
  { text: 'When was the last antibiotic?', category: 'medication', source: 'static', boostsOn: ['hasAntibiotics'] },
  { text: 'What was the last prescription for?', category: 'medication', source: 'static', boostsOn: ['hasPrescriptions'] },
  { text: 'How many prescriptions are on file?', category: 'medication', source: 'static', boostsOn: ['hasPrescriptions'] },
  { text: 'Which medications has {name} stopped?', category: 'medication', source: 'static', boostsOn: ['hasStoppedMeds'] },

  { text: "When was {name}'s last vaccine?", category: 'vaccine', source: 'static', boostsOn: ['hasVaccines'] },
  { text: 'Which vaccines has {name} received?', category: 'vaccine', source: 'static', boostsOn: ['hasVaccines'] },
  { text: 'How many vaccines are on file?', category: 'vaccine', source: 'static', boostsOn: ['hasVaccines'] },

  { text: 'When was the last lab test?', category: 'lab', source: 'static', boostsOn: ['hasLabs'] },
  { text: 'What were the most recent lab results?', category: 'lab', source: 'static', boostsOn: ['hasLabs'] },
  { text: 'How many lab tests are on file?', category: 'lab', source: 'static', boostsOn: ['hasLabs'] },
  { text: 'Were any recent labs flagged abnormal?', category: 'lab', source: 'static', boostsOn: ['hasAbnormalLabs'] },

  { text: 'When was the last doctor visit?', category: 'visit', source: 'static', boostsOn: ['hasVisits'] },
  { text: 'Which doctor has {name} seen most often?', category: 'visit', source: 'static', boostsOn: ['hasVisits'] },
  { text: 'How many doctor visits are on file?', category: 'visit', source: 'static', boostsOn: ['hasVisits'] },

  { text: "What's the most recent diagnosis?", category: 'diagnosis', source: 'static', boostsOn: ['hasDiagnoses'] },
  { text: 'What chronic conditions are on file?', category: 'diagnosis', source: 'static', boostsOn: ['hasChronicMeds', 'hasDiagnoses'] },
  { text: 'What are all the diagnoses on record?', category: 'diagnosis', source: 'static', boostsOn: ['hasDiagnoses'] },

  { text: 'What allergies does {name} have?', category: 'allergy', source: 'static', requires: ['hasAllergies'] },
  { text: 'Are there any allergies recorded?', category: 'allergy', source: 'static' },

  { text: 'When was the last scan or imaging?', category: 'scan', source: 'static', boostsOn: ['hasScans'] },
  { text: 'What did the most recent scan show?', category: 'scan', source: 'static', boostsOn: ['hasScans'] },

  { text: "What are {name}'s most recent vitals?", category: 'vitals', source: 'static', boostsOn: ['hasVitals'] },
  { text: "What's the latest blood pressure reading?", category: 'vitals', source: 'static', boostsOn: ['hasVitals'] },

  { text: "What's been added to the records this month?", category: 'general', source: 'static', boostsOn: ['hasRecentEvent'] },
  { text: 'How many records are on file?', category: 'general', source: 'static' },
  { text: 'What was the most recent record added?', category: 'general', source: 'static' },
  { text: "What's a summary of {name}'s recent records?", category: 'general', source: 'static', boostsOn: ['hasRecentEvent'] },
];

function normalizeText(text: string): string {
  return text.trim().toLowerCase();
}

const LEDGER_PREFIX = 'caremory:suggestion-clicks:';

export function readClickLedger(profileId: string): ClickLedger {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(LEDGER_PREFIX + profileId);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    return parsed as ClickLedger;
  } catch {
    return {};
  }
}

export function recordSuggestionClick(profileId: string, text: string): void {
  if (typeof window === 'undefined') return;
  const ledger = readClickLedger(profileId);
  const key = normalizeText(text);
  const prev = ledger[key];
  ledger[key] = {
    count: (prev?.count ?? 0) + 1,
    last_clicked_at: new Date().toISOString(),
  };
  try {
    window.localStorage.setItem(
      LEDGER_PREFIX + profileId,
      JSON.stringify(ledger),
    );
  } catch {
    // localStorage full or unavailable — clicks are non-critical, swallow.
  }
}

const CLICK_PENALTY = 40;
const FULL_PENALTY_DAYS = 7;
const ZERO_PENALTY_DAYS = 30;

function clickPenalty(clicks: ClickRecord | undefined, now: number): number {
  if (!clicks || clicks.count === 0) return 0;
  const last = new Date(clicks.last_clicked_at).getTime();
  if (Number.isNaN(last)) return 0;
  const daysSince = (now - last) / 86400000;
  if (daysSince < 0) return 0;
  if (daysSince < FULL_PENALTY_DAYS) return CLICK_PENALTY * clicks.count;
  if (daysSince < ZERO_PENALTY_DAYS) {
    const fade = 1 - (daysSince - FULL_PENALTY_DAYS) / (ZERO_PENALTY_DAYS - FULL_PENALTY_DAYS);
    return CLICK_PENALTY * clicks.count * fade;
  }
  return 0;
}

interface RankInputs {
  profile: Profile;
  facts: ProfileFacts;
  ledger: ClickLedger;
  now?: number;
  count?: number;
  personalized?: SuggestionEntry[];
}

export function rankSuggestions({
  profile,
  facts,
  ledger,
  now = Date.now(),
  count = 4,
  personalized = [],
}: RankInputs): RankedSuggestion[] {
  const name = firstName(profile.name);
  const candidates: SuggestionEntry[] = [...personalized, ...STATIC_POOL].map((entry) => ({
    ...entry,
    text: entry.text.replace(/\{name\}/g, name),
  }));

  const scored = candidates
    .filter((entry) => {
      if (!entry.requires) return true;
      return entry.requires.every((key) => facts[key]);
    })
    .map((entry) => {
      const base = entry.source === 'personalized' ? 100 : 50;
      const boostHits = entry.boostsOn?.filter((key) => facts[key]).length ?? 0;
      const boost = boostHits > 0 ? 25 : 0;
      const penalty = clickPenalty(ledger[normalizeText(entry.text)], now);
      const score = base + boost - penalty;
      return { entry, score, tiebreak: Math.random() };
    })
    .sort((a, b) => b.score - a.score || a.tiebreak - b.tiebreak);

  const seen = new Set<string>();
  const out: RankedSuggestion[] = [];
  for (const item of scored) {
    const key = normalizeText(item.entry.text);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({
      text: item.entry.text,
      category: item.entry.category,
      source: item.entry.source,
      score: Math.round(item.score),
    });
    if (out.length >= count) break;
  }
  return out;
}
